import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Heading, Text, Button, Badge, Spinner, Flex, VStack, HStack,
    Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, CardHeader,
    Alert, AlertIcon, Divider, SimpleGrid,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiGitMerge, FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';
import { apiService } from '../services/apiService';

const CHAMP_LABELS = {
    email: 'Email', telephone: 'Téléphone', date_naissance: 'Date de naissance',
    lieu_naissance: 'Lieu de naissance', adresse_postale: 'Adresse', code_postal: 'Code postal',
    ville: 'Ville', type_membre: 'Type membre', date_adhesion: 'Date d\'adhésion',
    numero_carte: 'N° carte', baptise: 'Baptisé', date_bapteme: 'Date baptême',
    eglise_bapteme: 'Église baptême', confirme: 'Confirmé', enrollment_status: 'Statut adhésion',
};

function ValeurCellule({ valeur }) {
    if (valeur === null || valeur === undefined || valeur === '') return <Text color="gray.300" fontSize="xs">–</Text>;
    if (valeur === 1 || valeur === true)  return <Badge colorScheme="green" fontSize="xs">Oui</Badge>;
    if (valeur === 0 || valeur === false) return <Badge colorScheme="gray"  fontSize="xs">Non</Badge>;
    const str = String(valeur);
    if (str.match(/^\d{4}-\d{2}-\d{2}/)) return <Text fontSize="xs">{new Date(str).toLocaleDateString('fr-FR')}</Text>;
    return <Text fontSize="xs">{str}</Text>;
}

export default function MemberMerge() {
    const [groupes, setGroupes]     = useState([]);
    const [loading, setLoading]     = useState(true);
    const [selected, setSelected]   = useState(null); // groupe de doublons sélectionné
    const [garder, setGarder]       = useState(null);
    const [supprimer, setSupprimer] = useState(null);
    const [merging, setMerging]     = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await apiService.makeRequest('/members/doublons');
            setGroupes(d.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [toast]);

    useEffect(() => { load(); }, [load]);

    const ouvrirFusion = (groupe) => {
        setSelected(groupe);
        // Par défaut : garder celui qui a le plus de données
        const sorted = [...groupe].sort((a, b) =>
            (parseInt(b.nb_cotisations) + parseInt(b.nb_groupes)) -
            (parseInt(a.nb_cotisations) + parseInt(a.nb_groupes))
        );
        setGarder(sorted[0]);
        setSupprimer(sorted[1]);
        onOpen();
    };

    const fusionner = async () => {
        if (!garder || !supprimer) return;
        setMerging(true);
        try {
            const d = await apiService.makeRequest('/members/fusionner', {
                method: 'POST',
                body: JSON.stringify({ garder_id: garder.id, supprimer_id: supprimer.id }),
            });
            toast({ title: 'Fusion réussie', description: d.message, status: 'success', duration: 5000 });
            onClose();
            load();
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 4000 });
        } finally { setMerging(false); }
    };

    // Champs à comparer entre les deux profils
    const champsCompares = garder && supprimer
        ? Object.keys(CHAMP_LABELS).filter(k => garder[k] !== supprimer[k])
        : [];

    return (
        <Box p={6} maxW="1200px" mx="auto">
            <Heading size="lg" mb={2}>Fusion des doublons</Heading>
            <Text color="gray.500" mb={6}>
                Membres ayant le même nom et prénom. Sélectionnez un groupe pour fusionner les profils.
            </Text>

            {loading && <Flex justify="center" py={10}><Spinner size="lg" /></Flex>}

            {!loading && groupes.length === 0 && (
                <Alert status="success">
                    <AlertIcon />
                    Aucun doublon détecté. Tous les membres ont un nom unique.
                </Alert>
            )}

            {!loading && groupes.length > 0 && (
                <VStack align="stretch" spacing={4}>
                    <Alert status="warning" mb={2}>
                        <AlertIcon />
                        {groupes.length} groupe{groupes.length > 1 ? 's' : ''} de doublons trouvés.
                        La fusion transfère cotisations et groupes vers le profil conservé, puis désactive le doublon.
                    </Alert>

                    {groupes.map((groupe, i) => (
                        <Card key={i} size="sm" border="1px solid" borderColor="orange.200">
                            <CardHeader pb={1}>
                                <Flex justify="space-between" align="center">
                                    <HStack>
                                        <FiAlertTriangle color="orange" />
                                        <Text fontWeight="700">{groupe[0].nom} {groupe[0].prenom}</Text>
                                        <Badge colorScheme="orange">{groupe.length} profils</Badge>
                                    </HStack>
                                    <Button size="sm" leftIcon={<FiGitMerge />} colorScheme="blue"
                                        onClick={() => ouvrirFusion(groupe)}>
                                        Fusionner
                                    </Button>
                                </Flex>
                            </CardHeader>
                            <CardBody pt={0}>
                                <Table size="xs" variant="simple">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th>ID</Th><Th>Email</Th><Th>Téléphone</Th>
                                            <Th>Inscription</Th><Th isNumeric>Cotisations</Th>
                                            <Th isNumeric>Groupes</Th><Th>Statut</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {groupe.map(m => (
                                            <Tr key={m.id}>
                                                <Td fontFamily="mono" fontSize="xs">#{m.id}</Td>
                                                <Td fontSize="xs">{m.email || <Text color="gray.300">–</Text>}</Td>
                                                <Td fontSize="xs">{m.telephone || <Text color="gray.300">–</Text>}</Td>
                                                <Td fontSize="xs">{m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : '–'}</Td>
                                                <Td isNumeric><Badge colorScheme={m.nb_cotisations > 0 ? 'green' : 'gray'}>{m.nb_cotisations}</Badge></Td>
                                                <Td isNumeric><Badge colorScheme={m.nb_groupes > 0 ? 'blue' : 'gray'}>{m.nb_groupes}</Badge></Td>
                                                <Td><Badge colorScheme={m.is_active ? 'green' : 'red'} fontSize="xx-small">{m.is_active ? 'actif' : 'inactif'}</Badge></Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    ))}
                </VStack>
            )}

            {/* Modal de fusion */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Fusionner — {selected?.[0]?.nom} {selected?.[0]?.prenom}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {garder && supprimer && (
                            <VStack align="stretch" spacing={4}>
                                <Alert status="info" fontSize="sm">
                                    <AlertIcon />
                                    Les champs vides du profil <strong>conservé</strong> seront complétés par les données du profil <strong>supprimé</strong>.
                                    Cotisations et groupes seront transférés.
                                </Alert>

                                {/* Sélection du profil à garder */}
                                <SimpleGrid columns={2} spacing={3}>
                                    {selected.map(m => (
                                        <Box key={m.id}
                                            border="2px solid"
                                            borderColor={garder?.id === m.id ? 'green.400' : 'gray.200'}
                                            borderRadius="md" p={3} cursor="pointer"
                                            onClick={() => { setGarder(m); setSupprimer(selected.find(x => x.id !== m.id)); }}
                                            bg={garder?.id === m.id ? 'green.50' : 'white'}>
                                            <HStack mb={1}>
                                                {garder?.id === m.id
                                                    ? <><FiCheck color="green" /><Badge colorScheme="green">Conserver</Badge></>
                                                    : <><FiX color="red" /><Badge colorScheme="red">Supprimer</Badge></>
                                                }
                                            </HStack>
                                            <Text fontWeight="700" fontSize="sm">#{m.id} — {m.matricule}</Text>
                                            <Text fontSize="xs" color="gray.500">{m.email || 'Pas d\'email'}</Text>
                                            <Text fontSize="xs" color="gray.500">{m.telephone || 'Pas de tél'}</Text>
                                            <HStack mt={1} spacing={2}>
                                                <Badge colorScheme="green" fontSize="xx-small">{m.nb_cotisations} cotisation(s)</Badge>
                                                <Badge colorScheme="blue" fontSize="xx-small">{m.nb_groupes} groupe(s)</Badge>
                                            </HStack>
                                        </Box>
                                    ))}
                                </SimpleGrid>

                                {/* Tableau des différences */}
                                {champsCompares.length > 0 && (
                                    <>
                                        <Divider />
                                        <Heading size="xs" color="gray.600">Différences entre les profils</Heading>
                                        <Box overflowX="auto">
                                            <Table size="sm" variant="simple">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th>Champ</Th>
                                                        <Th color="green.700">✓ Conservé (#{garder.id})</Th>
                                                        <Th color="red.600">✗ Supprimé (#{supprimer.id})</Th>
                                                        <Th>Résultat</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {champsCompares.map(k => {
                                                        const vGarder    = garder[k];
                                                        const vSupprimer = supprimer[k];
                                                        const estVide    = vGarder === null || vGarder === '' || vGarder === 0;
                                                        const resultat   = estVide ? vSupprimer : vGarder;
                                                        return (
                                                            <Tr key={k}>
                                                                <Td fontSize="xs" fontWeight="600">{CHAMP_LABELS[k] || k}</Td>
                                                                <Td bg={!estVide ? 'green.50' : undefined}><ValeurCellule valeur={vGarder} /></Td>
                                                                <Td bg={estVide && vSupprimer ? 'orange.50' : undefined}><ValeurCellule valeur={vSupprimer} /></Td>
                                                                <Td bg="blue.50" fontWeight="600"><ValeurCellule valeur={resultat} /></Td>
                                                            </Tr>
                                                        );
                                                    })}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                        <Text fontSize="xs" color="gray.400">
                                            Colonne "Résultat" : valeur du profil conservé si renseignée, sinon valeur du profil supprimé.
                                        </Text>
                                    </>
                                )}

                                {champsCompares.length === 0 && (
                                    <Alert status="success" fontSize="sm"><AlertIcon />Les deux profils ont des données identiques.</Alert>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
                        <Button colorScheme="blue" leftIcon={<FiGitMerge />} onClick={fusionner} isLoading={merging}>
                            Fusionner et supprimer le doublon
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
